'use client';

import { type ComponentProps } from 'react';
import {
  IconButton,
  Menu,
  Text,
  Tooltip,
  type InternalEnrichedFinding,
  formatFindingStatusForDisplay,
} from '@terra/ui-design-system';
import { Box, HStack } from '@terra/ui-styled-system';
import {
  FindingStatusSchema,
  type FindingStatusType,
} from '@terra/shared-schemas-orchestrator';
import { Portal } from '@ark-ui/react';
import {
  Tick01Icon,
  ArrowRight01Icon,
  Edit02Icon,
  FilePlusIcon,
  CheckListIcon,
  MoreVerticalCircle01Icon,
  SentIcon,
  Delete02Icon,
  type TerraIcon,
} from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import { rowClickBlockerProps } from './rowClickBlocker';

type FindingsRowActionsMenuProps = {
  finding: InternalEnrichedFinding;
  disabled?: boolean;
  disabledTooltip?: string;
  onEdit: (finding: InternalEnrichedFinding) => void;
  onDelete: (finding: InternalEnrichedFinding) => void;
  onChangeStatus: (
    finding: InternalEnrichedFinding,
    status: FindingStatusType,
  ) => void;
  onTogglePublish: (finding: InternalEnrichedFinding) => void;
  onGenerateReport: (finding: InternalEnrichedFinding) => void;
};
type MenuActionValue = 'toggle-publish' | 'generate-report' | 'edit' | 'delete';
type MenuActionItem = {
  value: MenuActionValue;
  icon: TerraIcon;
  label: string;
  onSelect: () => void;
  color?: ComponentProps<typeof Menu.Item>['color'];
};
const renderMenuActionItem = ({
  value,
  icon: Icon,
  label,
  onSelect,
  color,
}: MenuActionItem) => (
  <Menu.Item
    key={value}
    value={value}
    fontSize="xs"
    py="1"
    px="2"
    color={color}
    onClick={onSelect}
  >
    <HStack gap="1.5">
      <Icon size={12} />
      <Text fontSize="xs">{label}</Text>
    </HStack>
  </Menu.Item>
);

export function FindingsRowActionsMenu({
  finding,
  disabled = false,
  disabledTooltip,
  onEdit,
  onDelete,
  onChangeStatus,
  onTogglePublish,
  onGenerateReport,
}: FindingsRowActionsMenuProps) {
  const t = useTranslations('findingsTab.row');
  const isPublished = !!finding.isPublished;
  const currentStatus = finding.status;
  const actionItems: MenuActionItem[] = [
    {
      value: 'toggle-publish',
      icon: SentIcon,
      label: isPublished ? t('unpublish') : t('publish'),
      onSelect: () => onTogglePublish(finding),
    },
    {
      value: 'generate-report',
      icon: FilePlusIcon,
      label: t('generateReport'),
      onSelect: () => onGenerateReport(finding),
    },
  ];
  const destructiveItems: MenuActionItem[] = [
    {
      value: 'edit',
      icon: Edit02Icon,
      label: t('edit'),
      onSelect: () => onEdit(finding),
    },
    {
      value: 'delete',
      icon: Delete02Icon,
      label: t('delete'),
      onSelect: () => onDelete(finding),
      color: 'fg.error',
    },
  ];

  return (
    <Box
      h="full"
      display="flex"
      alignItems="center"
      justifyContent="center"
      {...rowClickBlockerProps}
    >
      {disabled ? (
        <Tooltip title={disabledTooltip ?? t('menuTooltipDisabled')}>
          <IconButton
            aria-label={t('menuTooltip')}
            size="xs"
            variant="ghost"
            disabled
          >
            <MoreVerticalCircle01Icon size={14} />
          </IconButton>
        </Tooltip>
      ) : (
        <Menu.Root
          lazyMount
          unmountOnExit
          positioning={{
            strategy: 'fixed',
            placement: 'bottom-end',
            offset: { mainAxis: 4, crossAxis: 0 },
          }}
        >
          <Menu.Trigger asChild>
            <IconButton aria-label={t('menuTooltip')} size="xs" variant="ghost">
              <MoreVerticalCircle01Icon size={14} />
            </IconButton>
          </Menu.Trigger>
          <Portal>
            <Menu.Positioner>
              <Menu.Content minW="160px" py="1">
                <Menu.Root
                  positioning={{
                    strategy: 'fixed',
                    placement: 'right-start',
                  }}
                  onSelect={(details) => {
                    const parsed = FindingStatusSchema.safeParse(details.value);

                    if (!parsed.success) return;
                    onChangeStatus(finding, parsed.data);
                  }}
                >
                  <Menu.TriggerItem fontSize="xs" py="1" px="2">
                    <HStack gap="1.5" flexGrow={1}>
                      <CheckListIcon size={12} />
                      <Text fontSize="xs">{t('changeStatus')}</Text>
                    </HStack>
                    <Menu.Indicator>
                      <ArrowRight01Icon size={12} />
                    </Menu.Indicator>
                  </Menu.TriggerItem>
                  <Portal>
                    <Menu.Positioner>
                      <Menu.Content minW="150px" py="1">
                        {FindingStatusSchema.options.map((status) => (
                          <Menu.Item
                            key={status}
                            value={status}
                            fontSize="xs"
                            py="1"
                            px="2"
                          >
                            <HStack gap="1.5" alignItems="center" w="full">
                              <Box w="12px" h="12px" flexShrink={0}>
                                {status === currentStatus ? (
                                  <Tick01Icon size={12} />
                                ) : null}
                              </Box>
                              <Text fontSize="xs">
                                {formatFindingStatusForDisplay(status)}
                              </Text>
                            </HStack>
                          </Menu.Item>
                        ))}
                      </Menu.Content>
                    </Menu.Positioner>
                  </Portal>
                </Menu.Root>

                {actionItems.map(renderMenuActionItem)}

                <Menu.Separator />

                {destructiveItems.map(renderMenuActionItem)}
              </Menu.Content>
            </Menu.Positioner>
          </Portal>
        </Menu.Root>
      )}
    </Box>
  );
}
